import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import { INPUT_TYPES } from '../../../constants';
import { removePlusSignSuffix } from '../../../utils/removePlusSignSuffix';
import { removeHyphensAndCapitalize } from '../../../utils/removeHyphensAndCapitalize';
import BaseForm from './BaseForm';
import { AutomatedFormProps } from './types';

const AutomatedForm: React.FC<AutomatedFormProps> = React.memo(
  ({
    fields,
    editFields,
    currentRecord,
    onSubmit,
    onDelete,
    canDelete = true,
    isMatrix = false,
    formInstance,
  }) => {
    // Memoize fields to use and apply conditional disabled state
    const fieldsToUse = useMemo(() => {
      const baseFields = currentRecord && editFields ? editFields : fields;

      // Apply conditional disabled state for specific fields
      return baseFields?.map((field: any) => {
        // Disable applicationType field when editing existing records
        if (field.key === 'applicationType' && currentRecord) {
          return { ...field, disabled: true };
        }
        return field;
      });
    }, [currentRecord, editFields, fields]);

    const form = useForm();

    const formInstanceToUse = useMemo(
      () => formInstance ?? form,
      [formInstance, form],
    );

    const { reset } = formInstanceToUse;

    useEffect(() => {
      let defaultValues = { ...currentRecord };

      if (!isMatrix) {
        defaultValues = fieldsToUse?.reduce(
          (acc, field) => {
            if (currentRecord) {
              acc[field.key] = currentRecord[field.key];

              if (field.type === INPUT_TYPES.TIME) {
                acc[field.key] = currentRecord[field.key] ?? 0;
              }

              if (field.type === INPUT_TYPES.SELECT) {
                acc[field.key] = currentRecord[field.key] ?? '';

                if (acc[field.key] !== '')
                  acc[field.key] = removeHyphensAndCapitalize(
                    removePlusSignSuffix(currentRecord[field.key]),
                  );
              }

              if (field.type === INPUT_TYPES.TIER_SELECT) {
                acc[field.key] = currentRecord[field.key] ?? '';
              }

              if (field.type === INPUT_TYPES.DATE) {
                acc[field.key] = currentRecord[field.key]
                  ? new Date(currentRecord[field.key])
                      .toISOString()
                      .split('T')[0]
                  : '';
              }
            } else {
              acc[field.key] = '';
            }
            return acc;
          },
          {} as { [key: string]: string },
        );
      }

      reset((prev) => ({ ...prev, ...defaultValues }));
    }, [currentRecord]);

    // Effect to reset when no current record
    useEffect(() => {
      if (!currentRecord) reset();
    }, [currentRecord, reset]);

    return (
      <BaseForm
        formInstance={formInstanceToUse}
        fields={fieldsToUse}
        currentRecord={currentRecord}
        onSubmit={onSubmit}
        canDelete={canDelete}
        onDelete={onDelete}
        resetForm={reset}
      />
    );
  },
);

export default AutomatedForm;
